import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { type IFile } from "src/interfaces";
import { getUserId, titlecaseSentence } from "src/utils";

interface IPendingRequest {
  req: string;
  name: string;
  data: any;
  status: string;
  created: number;
  uploaded: string;
  action: string;
  lbname?: string;
}

interface IPendingNewEntry {
  id: string;
  lbid: string;
  created: number;
}

interface IPendingFiles {
  entryId: string;
  files: IFile[];
}

interface PendingRequestsSlice {
  pendingRequests: IPendingRequest[];
  pendingWithdraws: string[];
  pendingNewEntries: IPendingNewEntry[];
  pendingFiles: IPendingFiles[];
  pendingVerify: Array<{
    lbeid: string | null;
    partid: string | null;
    meid: string | null;
  }>;
}

const initialState: PendingRequestsSlice = {
  pendingRequests: [],
  pendingWithdraws: [],
  pendingNewEntries: [],
  pendingFiles: [],
  pendingVerify: [],
};

export const pendingRequestsSlice = createSlice({
  name: "verifySlice",
  initialState,
  reducers: {
    setPendingRequests: (state, action: PayloadAction<IPendingRequest[]>) => {
      const pendingRequests: IPendingRequest[] = [];
      const pendingWithdraws: string[] = [];
      const pendingNewEntries: IPendingNewEntry[] = [];
      const newEntriesIds: string[] = [];
      const pendingFiles: IPendingFiles[] = [];
      let pendingVerify: Array<{
        lbeid: string | null;
        partid: string | null;
        meid: string | null;
      }> = [];

      action.payload.forEach(pending => {
        const body = JSON.parse(pending.data);
        const splitted = pending.req.split("__");
        const request = splitted[0];
        const id = splitted[1];
        pendingRequests.push({
          ...pending,
          action: titlecaseSentence(request.replaceAll("-", " ")),
          lbname: body._lbname,
        });
        if (request === "withdraw-entry") {
          pendingWithdraws.push(id);
        } else if (request === "save-entry") {
          if (id.slice(0, 4) === "new=") {
            const lbid = id.split("&&")[1].slice(5);
            if (!newEntriesIds.includes(id)) {
              pendingNewEntries.push({
                id,
                lbid,
                created: pending.created,
              });
              newEntriesIds.push(id);
            }
          }
        } else if (request === "upload-file") {
          const entryId = body.meid ?? body.partid ?? body.lbeid;
          const files = body.files.map((f: any) => ({
            aid: Math.random(),
            date: new Date(pending.created),
            filename: f.name,
            type: f.type,
            deleted: 0,
            local: true,
          }));
          pendingFiles.push({ entryId, files });
        } else if (request === "verify-entry") {
          if (body.params.user === getUserId()) {
            pendingVerify = [...pendingVerify, ...body.params.list];
          }
        }
      });
      state.pendingRequests = pendingRequests;
      state.pendingWithdraws = pendingWithdraws;
      state.pendingNewEntries = pendingNewEntries;
      state.pendingFiles = pendingFiles;
      state.pendingVerify = pendingVerify;
    },
  },
});

export const { setPendingRequests } = pendingRequestsSlice.actions;

export default pendingRequestsSlice.reducer;
